<script setup lang="ts">

import { ref } from "vue"
import { currencyOptions, conditionOptions } from "@/composables/inventoryHelpers"
import type { AcInventoryBatch } from "@/types/inventory"

const props = defineProps<{
    quantityLabel?: string,
    defaultCurrency?: NonNullable<AcInventoryBatch["purchaseCurrency"]>,
    integerOnly?: boolean,
}>()

const emit = defineEmits(["batch-submitted", "cancel"])

const todayISO = new Date().toISOString().slice(0, 10)

const quantity = ref<number | null>(null)
const unitPrice = ref<number | null>(null)
const purchaseCurrency = ref<NonNullable<AcInventoryBatch["purchaseCurrency"]>>(props.defaultCurrency ?? "TRY")
const condition = ref<NonNullable<AcInventoryBatch["condition"]>>("NEW")
const purchaseDate = ref<string>(todayISO)
const note = ref("")

const submit = () => {
    if (quantity.value === null || isNaN(Number(quantity.value)) || Number(quantity.value) <= 0) {
        alert("Lütfen geçerli bir miktar giriniz.")
        return
    }
    if (props.integerOnly && !Number.isInteger(Number(quantity.value))) {
        alert("Bu ürün adet ile takip edilir; lütfen tam sayı giriniz.")
        return
    }
    if (unitPrice.value === null || isNaN(Number(unitPrice.value)) || Number(unitPrice.value) < 0) {
        alert("Lütfen geçerli bir birim fiyat giriniz.")
        return
    }
    emit("batch-submitted", {
        quantity: Number(quantity.value),
        unitPrice: Number(unitPrice.value),
        purchaseCurrency: purchaseCurrency.value,
        condition: condition.value,
        purchaseDate: new Date(purchaseDate.value),
        note: note.value.trim() || undefined,
    })
}

</script>

<template>
    <form @submit.prevent="submit" class="ac-main-form !border-none">
        <div class="flex gap-4">
            <div class="ac-form-control w-full">
                <label for="batchQuantity">Miktar{{ quantityLabel ? ` (${quantityLabel})` : '' }}</label>
                <input id="batchQuantity" type="number" min="1" :step="integerOnly ? '1' : 'any'" v-model="quantity" required>
            </div>
            <div class="ac-form-control w-full">
                <label for="batchUnitPrice">Birim Alış Fiyatı</label>
                <input id="batchUnitPrice" type="number" min="0" step="any" v-model="unitPrice" required>
            </div>
        </div>

        <div class="flex gap-4">
            <div class="ac-form-control w-full">
                <label for="batchCurrency">Para Birimi</label>
                <select id="batchCurrency" v-model="purchaseCurrency" class="!py-3">
                    <option v-for="opt in currencyOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </select>
            </div>
            <div class="ac-form-control w-full">
                <label for="batchCondition">Durum</label>
                <select id="batchCondition" v-model="condition" class="!py-3">
                    <option v-for="opt in conditionOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </select>
            </div>
            <div class="ac-form-control w-full">
                <label for="batchDate">Alış Tarihi</label>
                <input id="batchDate" type="date" v-model="purchaseDate">
            </div>
        </div>

        <div class="ac-form-control w-full">
            <label for="batchNote">Not</label>
            <input id="batchNote" type="text" v-model="note">
            <span class="optional-label">opsiyonel</span>
        </div>

        <div class="flex items-center justify-center gap-6 w-full">
            <button type="submit" class="ac-primary-button text-2xl mt-2">Parti Ekle</button>
            <button type="button" @click="$emit('cancel')" class="ac-secondary-button text-2xl mt-2">İptal</button>
        </div>
    </form>
</template>

<style scoped>
.optional-label {
    font-size: 1.1rem;
    color: #9ca3af;
    margin-top: 0.25rem;
    font-style: italic;
}
</style>
